import React from 'react';
import { useLang } from '../../contexts/LangContext';
import { UNASSIGNED_APARTMENT_GROUP_ID } from '../../constants/apartmentGroups';

export interface ApartmentGroupOption {
  id: string;
  name: string;
}

interface ApartmentGroupMultiSelectProps {
  groups: ApartmentGroupOption[];
  selectedGroupIds: string[];
  onChange: (groupIds: string[]) => void;
  disabled?: boolean;
  helperText?: string;
}

const ApartmentGroupMultiSelect: React.FC<ApartmentGroupMultiSelectProps> = ({
  groups,
  selectedGroupIds,
  onChange,
  disabled = false,
  helperText,
}) => {
  const { t } = useLang();

  const toggleGroup = (groupId: string) => {
    if (disabled) {
      return;
    }
    onChange(
      selectedGroupIds.includes(groupId)
        ? selectedGroupIds.filter((id) => id !== groupId)
        : [...selectedGroupIds, groupId],
    );
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('selectApartmentGroups')}
      </label>
      <div className={`border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 ${disabled ? 'bg-gray-100 opacity-70' : ''}`}>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={selectedGroupIds.includes(UNASSIGNED_APARTMENT_GROUP_ID)}
            onChange={() => toggleGroup(UNASSIGNED_APARTMENT_GROUP_ID)}
            disabled={disabled}
            className="rounded border-gray-300 text-blue-600"
          />
          <span className="font-medium text-gray-800">{t('unassignedApartmentGroup')}</span>
        </label>
        {groups.map((group) => (
          <label key={group.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selectedGroupIds.includes(group.id)}
              onChange={() => toggleGroup(group.id)}
              disabled={disabled}
              className="rounded border-gray-300 text-blue-600"
            />
            <span>{group.name}</span>
          </label>
        ))}
        {groups.length === 0 && (
          <p className="text-sm text-gray-500">{t('noApartmentGroups')}</p>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        {helperText || t('apartmentGroupMultiSelectHint')}
      </p>
    </div>
  );
};

export default ApartmentGroupMultiSelect;
